import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from 'react-router-dom';
import { useAuth, useBusinessUnit } from '@/App';
import {
  LayoutDashboard,
  DollarSign,
  Users,
  FileText,
  Receipt,
  LogOut,
  Building2,
  Settings,
  UserCircle,
  Home,
  Briefcase,
  Upload,
  PieChart,
  UserPlus,
  BarChart3,
  ScrollText,
  Bell,
  Mail,
  Calculator,
  FileCheck,
  Store,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';

// Admin navigation items
const adminNavItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/commissions', icon: DollarSign, label: 'Commissions' },
  { path: '/agents', icon: Users, label: 'Agents' },
  { path: '/statements', icon: FileText, label: 'Statements' },
  { path: '/expenses', icon: Receipt, label: 'Expenses' },
  { path: '/vendors', icon: Store, label: 'Vendors' },
  { path: '/referral-leads', icon: UserPlus, label: 'Referral Leads' },
  { path: '/reports', icon: BarChart3, label: 'Reports' },
  { path: '/notifications', icon: Bell, label: 'Notifications' },
  { path: '/audit-logs', icon: ScrollText, label: 'Audit Logs' },
  { path: '/email-templates', icon: Mail, label: 'Email Templates' },
  { path: '/import', icon: Upload, label: 'Import Data' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

// Agent navigation items (limited access)
const agentNavItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/commissions', icon: DollarSign, label: 'My Commissions' },
  { path: '/expenses', icon: Receipt, label: 'My Expenses' },
  { path: '/statements', icon: FileText, label: 'My Statements' },
  { path: '/my-tax-slips', icon: FileCheck, label: 'My Tax Slips' },
  { path: '/referral-leads', icon: UserPlus, label: 'My Referrals' },
  { path: '/notifications', icon: Bell, label: 'Notifications' },
  { path: '/settings', icon: Settings, label: 'Settings' },
];

// Bottom nav items (most used on mobile)
const bottomNavItemsAdmin = [
  { path: '/', icon: LayoutDashboard, label: 'Home' },
  { path: '/commissions', icon: DollarSign, label: 'Comm.' },
  { path: '/statements', icon: FileText, label: 'Stmt.' },
  { path: '/agents', icon: Users, label: 'Agents' },
];

const bottomNavItemsAgent = [
  { path: '/', icon: LayoutDashboard, label: 'Home' },
  { path: '/commissions', icon: DollarSign, label: 'Comm.' },
  { path: '/statements', icon: FileText, label: 'Stmt.' },
  { path: '/notifications', icon: Bell, label: 'Alerts' },
];

export function MobileHeader({ onMenuClick }) {
  const { businessUnit } = useBusinessUnit();
  
  return (
    <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-slate-900 border-b border-slate-800 px-4 py-3">
      <div className="flex items-center justify-between">
        <button
          onClick={onMenuClick}
          className="p-2 -ml-2 text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
          data-testid="mobile-menu-btn"
        >
          <Menu className="w-6 h-6" />
        </button>
        
        <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
          Utmost Financial
        </span>
        
        <div className={`px-2 py-1 rounded-full text-xs font-medium ${
          businessUnit === 'mortgage' 
            ? 'bg-emerald-500/20 text-emerald-300' 
            : 'bg-blue-500/20 text-blue-300'
        }`}>
          {businessUnit === 'mortgage' ? 'MTG' : 'INS'}
        </div>
      </div>
    </header>
  );
}

export function MobileDrawer({ isOpen, onClose }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { businessUnit, switchBusinessUnit } = useBusinessUnit();
  
  const isAgent = user?.role === 'agent';
  const navItems = isAgent ? agentNavItems : adminNavItems;

  // Close drawer on navigation
  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  // Handle logout
  const handleLogout = () => {
    logout();
    onClose();
  };

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="lg:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
        data-testid="mobile-drawer-backdrop"
      />
      
      {/* Drawer */}
      <div 
        className="lg:hidden fixed inset-y-0 left-0 z-50 w-80 max-w-[85vw] bg-slate-900 shadow-2xl transform transition-transform duration-300 ease-out overflow-y-auto"
        style={{ transform: isOpen ? 'translateX(0)' : 'translateX(-100%)' }}
        data-testid="mobile-drawer"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <span className="text-white font-bold text-lg" style={{ fontFamily: 'Manrope' }}>
            Utmost Financial Inc.
          </span>
          <button
            onClick={onClose}
            className="p-2 text-slate-400 hover:text-white hover:bg-white/10 rounded-lg transition-colors touch-manipulation"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* User Info */}
        {user && (
          <div className="p-4 border-b border-slate-800">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-slate-700 flex items-center justify-center">
                <UserCircle className="w-7 h-7 text-slate-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user.name}</p>
                <p className="text-slate-400 text-sm truncate">{user.email}</p>
                <span className={`inline-flex items-center gap-1 mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  isAgent 
                    ? 'bg-emerald-500/20 text-emerald-300' 
                    : 'bg-violet-500/20 text-violet-300'
                }`}>
                  {isAgent ? 'Agent' : 'Admin'}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Business Unit Selector */}
        <div className="p-4 border-b border-slate-800">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Business Unit</p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => switchBusinessUnit('mortgage')}
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-all touch-manipulation ${
                businessUnit === 'mortgage'
                  ? 'bg-emerald-500/20 text-emerald-300 border border-emerald-500/30'
                  : 'text-slate-400 hover:text-white bg-slate-800/50'
              }`}
            >
              <Home className="w-4 h-4" />
              <span>Mortgage</span>
            </button>
            <button
              onClick={() => switchBusinessUnit('insurance')}
              className={`flex items-center justify-center gap-2 px-3 py-3 rounded-lg text-sm font-medium transition-all touch-manipulation ${
                businessUnit === 'insurance'
                  ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30'
                  : 'text-slate-400 hover:text-white bg-slate-800/50'
              }`}
            >
              <Briefcase className="w-4 h-4" />
              <span>Insurance</span>
            </button>
          </div>
        </div>

        {/* Admin Quick Links */}
        {!isAgent && (
          <div className="p-4 border-b border-slate-800">
            <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Quick Access</p>
            <div className="space-y-1">
              <button
                onClick={() => handleNavClick('/portal')}
                className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium transition-all touch-manipulation ${
                  location.pathname === '/portal'
                    ? 'bg-violet-500/20 text-violet-300'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <PieChart className="w-5 h-5" />
                <span>Portal Overview</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </button>
              <button
                onClick={() => handleNavClick('/accounting')}
                className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium transition-all touch-manipulation ${
                  location.pathname === '/accounting'
                    ? 'bg-blue-500/20 text-blue-300'
                    : 'text-slate-400 hover:text-white hover:bg-white/5'
                }`}
              >
                <Calculator className="w-5 h-5" />
                <span>Accounting</span>
                <ChevronRight className="w-4 h-4 ml-auto" />
              </button>
            </div>
          </div>
        )}

        {/* Navigation */}
        <nav className="p-4">
          <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Menu</p>
          <div className="space-y-1">
            {navItems.map((item) => {
              const isActive = location.pathname === item.path;
              return (
                <button
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`flex items-center gap-3 w-full px-3 py-3 rounded-lg text-sm font-medium transition-all touch-manipulation ${
                    isActive
                      ? 'bg-white/15 text-white border-l-3 border-emerald-500'
                      : 'text-slate-300 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {isActive && <ChevronRight className="w-4 h-4 ml-auto text-emerald-400" />}
                </button>
              );
            })}
          </div>
        </nav>

        {/* Logout */}
        <div className="p-4 border-t border-slate-800 mt-auto">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 w-full px-3 py-3 rounded-lg text-red-400 hover:text-red-300 hover:bg-red-500/10 transition-colors text-sm font-medium touch-manipulation"
          >
            <LogOut className="w-5 h-5" />
            <span>Logout</span>
          </button>
        </div>
      </div>
    </>
  );
}

export function BottomNav() {
  const location = useLocation();
  const { user } = useAuth();
  
  const isAgent = user?.role === 'agent';
  const bottomItems = isAgent ? bottomNavItemsAgent : bottomNavItemsAdmin;

  return (
    <nav 
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-slate-200"
      data-testid="bottom-nav"
      style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
    >
      <div className="flex items-center justify-around h-14 mb-1">
        {bottomItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full px-2 transition-colors touch-manipulation relative ${
                isActive ? 'text-emerald-600' : 'text-slate-500'
              }`}
              data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            >
              <item.icon className={`w-5 h-5 mb-0.5 ${isActive ? 'stroke-2' : ''}`} />
              <span className={`text-[10px] ${isActive ? 'font-semibold' : 'font-medium'}`}>
                {item.label}
              </span>
              {isActive && (
                <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-8 h-0.5 bg-emerald-500 rounded-b-full" />
              )}
            </Link>
          );
        })}
      </div>
    </nav>
  );
}

export default function MobileNav() {
  const [drawerOpen, setDrawerOpen] = useState(false);

  return (
    <>
      <MobileHeader onMenuClick={() => setDrawerOpen(true)} />
      <MobileDrawer isOpen={drawerOpen} onClose={() => setDrawerOpen(false)} />
      <BottomNav />
    </>
  );
}
